/**
 * Everything outside the arena shell: the bowl, the crowd, the roof, the
 * floodlights, the city and the sky.
 *
 * WHY THIS EXISTS. The arena walls are glass (see `makeArenaMaterials`), and
 * glass with nothing behind it is grey paint. The single biggest change to how
 * this game looks is not the walls' shader — it is that there is now something
 * on the other side of them. Every decision below follows from that: the lower
 * tier front row is placed at the height a chase camera at car level sees
 * through the wall, the LED ring sits where it will be read through the glass
 * at pitch level, and the floodlights are at the corners because that is where
 * the environment map already put them.
 *
 * DRAW CALLS ARE THE BUDGET, NOT TRIANGLES. Up to six cars, three particle
 * systems and this all render together. So: the crowd is one `InstancedMesh`,
 * the city is one, the roof truss is one, every ring of structure is a single
 * lofted mesh rather than a per-segment mesh, and the whole environment adds
 * roughly a dozen draw calls. It would have been trivial to write this as a few
 * thousand `Mesh` objects and it would have been unplayable.
 *
 * NOTHING HERE IS AN ASSET. Every texture is drawn into a canvas or written
 * into a typed array at build time. Nothing runs at module scope, so the module
 * imports cleanly in Node and the layout maths is tested there.
 */

import {
  AdditiveBlending,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  DoubleSide,
  Euler,
  FrontSide,
  Group,
  InstancedMesh,
  LinearFilter,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  Quaternion,
  RepeatWrapping,
  SRGBColorSpace,
  Vector3,
} from 'three';
import { ARENA } from '../physics/constants';
import { TEAM_COLORS } from './materials';
import {
  bowlPoint,
  bowlSamples,
  loftBowl,
  steppedProfile,
  type BowlProfilePoint,
  type BowlSample,
} from './bowl';
import { TIERS, buildCrowd, type CrowdVisual } from './crowd';
import {
  buildSkyline,
  buildStars,
  makeSkyTexture,
  type SkylineVisual,
  type StarFieldVisual,
} from './sky';

// ---------------------------------------------------------------------------
// Dimensions
// ---------------------------------------------------------------------------

const LOWER = TIERS[0];
const UPPER = TIERS[1];

const lowerBack = {
  offset: LOWER.frontOffset + LOWER.rows * LOWER.run,
  y: LOWER.frontY + LOWER.rows * LOWER.rise,
};
const upperBack = {
  offset: UPPER.frontOffset + UPPER.rows * UPPER.run,
  y: UPPER.frontY + UPPER.rows * UPPER.rise,
};

/**
 * Every dimension of the stadium, in uu, THREE frame. Exported so the tests can
 * assert clearances against the same numbers the builder uses rather than
 * against a second copy that will drift.
 */
export const STADIUM = {
  /** How many points each ring of the bowl is lofted from. 176 is the point
   *  where the corner arcs stop reading as facets from the far touchline. */
  samples: 176,

  /** Flat trackside apron between the wall and the front of the stand. */
  apronOffset: 330,

  lowerFront: { offset: LOWER.frontOffset, y: LOWER.frontY },
  lowerBack,
  /** Vertical band above the lower tier: the sponsor/banner ring. */
  fasciaTopY: lowerBack.y + 390,
  /** Ledge between the fascia and the front of the upper tier. */
  upperFront: { offset: UPPER.frontOffset, y: UPPER.frontY },
  upperBack,
  /** Parapet above the back row of the upper tier. */
  parapetY: upperBack.y + 190,
  /** Outer skin of the bowl, from the ground to the top of the back wall. */
  outerOffset: upperBack.offset + 170,
  /** Top of the back wall. It has to reach the roof: leave a gap here and you
   *  can see stars through it from the pitch, in a covered stadium. */
  outerTopY: 3810,

  /** Roof: inner (overhang) edge, outer edge, and how far the fascia drops. */
  roofInner: { offset: 1700, y: 3320 },
  roofOuter: { offset: 4320, y: 3820 },
  roofFasciaDrop: 460,

  /** LED advertising ring, right against the glass. */
  ledInner: { offset: 140, y: 8 },
  ledOuter: { offset: 210, y: 250 },

  /** Floodlight masts, at the four 45-degree corner runs. */
  mastOffset: 4620,
  mastHeight: 4900,
  mastHeadY: 4150,

  /** Big screens hung from the roof at each end. */
  screenOffset: 1790,
  screenCentreY: 2680,
  screenWidth: 3300,
  screenHeight: 1120,

  /** Overhead catwalk rig, spanning the pitch above the arena ceiling. This is
   *  what the player sees when the camera goes up the wall and looks back — the
   *  ceiling is glass, and behind glass there has to be something. */
  catwalkY: 2960,
  catwalkOverhang: 1700,

  /** Ground plane the whole thing stands on. */
  groundRadius: 26000,
  groundY: -24,
} as const;

const PALETTE_STADIUM = {
  concrete: 0x3d414d,
  concreteDark: 0x161a24,
  steel: 0x454d61,
  // Not black. The underside of the roof is the top third of every shot taken
  // from inside the bowl, and at 0x141824 it was an unlit void that made the
  // floodlight towers look like they were floating in space.
  roof: 0x262b38,
  parapet: 0x1d2230,
  lamp: 0xfff4dc,
} as const;

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/** Build a non-indexed geometry from a lofted profile, with flat normals. */
const loftGeometry = (
  profile: readonly BowlProfilePoint[],
  samples: readonly BowlSample[]
): BufferGeometry => {
  const positions = loftBowl(profile, samples);
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

/**
 * The bowl's cross-section, from the edge of the pitch to the ground outside.
 *
 * Read it top to bottom and it is the walk a spectator takes: across the
 * trackside apron, up the front wall of the lower tier, up eleven steps, past
 * the banner fascia, along the ledge, up thirteen more steps, over the parapet
 * and down the outside of the building.
 */
export const bowlProfile = (): BowlProfilePoint[] => [
  { offset: 0, y: 0 },
  { offset: STADIUM.apronOffset, y: 0 },
  { offset: STADIUM.lowerFront.offset, y: STADIUM.lowerFront.y },
  ...steppedProfile(
    STADIUM.lowerFront.offset,
    STADIUM.lowerFront.y,
    LOWER.rows,
    LOWER.run,
    LOWER.rise
  ).slice(1),
  { offset: STADIUM.lowerBack.offset, y: STADIUM.fasciaTopY },
  { offset: STADIUM.upperFront.offset, y: STADIUM.fasciaTopY },
  { offset: STADIUM.upperFront.offset, y: STADIUM.upperFront.y },
  ...steppedProfile(
    STADIUM.upperFront.offset,
    STADIUM.upperFront.y,
    UPPER.rows,
    UPPER.run,
    UPPER.rise
  ).slice(1),
  { offset: STADIUM.upperBack.offset, y: STADIUM.parapetY },
  { offset: STADIUM.outerOffset - 70, y: STADIUM.parapetY },
  // Up the inside of the back wall to meet the roof, over the top, and down
  // the outside to the ground.
  { offset: STADIUM.outerOffset - 70, y: STADIUM.outerTopY },
  { offset: STADIUM.outerOffset, y: STADIUM.outerTopY },
  { offset: STADIUM.outerOffset, y: STADIUM.groundY },
];

/** Roof cross-section: a shallow outward rake with a fascia dropped at the rim. */
export const roofProfile = (): BowlProfilePoint[] => [
  { offset: STADIUM.roofInner.offset, y: STADIUM.roofInner.y },
  { offset: STADIUM.roofOuter.offset, y: STADIUM.roofOuter.y },
  { offset: STADIUM.roofOuter.offset, y: STADIUM.roofOuter.y - STADIUM.roofFasciaDrop },
];

// ---------------------------------------------------------------------------
// Textures
// ---------------------------------------------------------------------------

/**
 * Sponsor/banner ring. Abstract on purpose — legible words would be a brand,
 * and blocks of colour at 3,000 uu read exactly the same way.
 */
const makeBannerTexture = (): CanvasTexture => {
  const w = 1024;
  const h = 64;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  ctx.fillStyle = '#0c111c';
  ctx.fillRect(0, 0, w, h);

  const panels = 16;
  // Dark and close in value. A high-chroma banner ring wraps a rainbow round
  // the whole bowl and becomes the brightest thing on screen, which is not what
  // advertising boards do at 3,000 uu under a floodlight.
  const tints = ['#1d283c', '#2c3346', '#242f44', '#352c44', '#20353c', '#332d24'];
  for (let i = 0; i < panels; i++) {
    const x = (i * w) / panels;
    ctx.fillStyle = tints[i % tints.length];
    ctx.fillRect(x + 4, 8, w / panels - 8, h - 16);
    // A bright bar inside each panel: the "lit board" read at distance.
    ctx.fillStyle = 'rgba(170,196,238,0.46)';
    ctx.fillRect(x + 14, h * 0.36, (w / panels) * (0.2 + ((i * 7) % 5) * 0.11), h * 0.2);
  }
  // Top and bottom edge lines so the band has a hard silhouette.
  ctx.fillStyle = 'rgba(150,180,225,0.22)';
  ctx.fillRect(0, 0, w, 3);
  ctx.fillRect(0, h - 3, w, 3);

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
};

/** The big screens: an abstract broadcast frame, never real text. */
const makeScreenTexture = (): CanvasTexture => {
  const w = 512;
  const h = 256;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  const bg = ctx.createLinearGradient(0, 0, 0, h);
  bg.addColorStop(0, '#12203c');
  bg.addColorStop(1, '#060a14');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  // A wash of the pitch, as if the screen were showing the match.
  ctx.fillStyle = 'rgba(40,70,120,0.55)';
  ctx.beginPath();
  ctx.ellipse(w / 2, h * 0.62, w * 0.42, h * 0.3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(220,236,255,0.5)';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(w / 2, h * 0.62, w * 0.16, h * 0.11, 0, 0, Math.PI * 2);
  ctx.stroke();

  // Scorebug: two team blocks and a neutral clock between them.
  const bugY = 12;
  const bugH = 40;
  ctx.fillStyle = '#2a7dff';
  ctx.fillRect(w * 0.3, bugY, w * 0.12, bugH);
  ctx.fillStyle = '#ff8a1f';
  ctx.fillRect(w * 0.58, bugY, w * 0.12, bugH);
  ctx.fillStyle = 'rgba(12,18,30,0.9)';
  ctx.fillRect(w * 0.42, bugY, w * 0.16, bugH);
  ctx.fillStyle = 'rgba(230,240,255,0.85)';
  ctx.fillRect(w * 0.455, bugY + 12, w * 0.09, bugH - 24);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.magFilter = LinearFilter;
  return texture;
};

// ---------------------------------------------------------------------------
// Sub-assemblies
// ---------------------------------------------------------------------------

/**
 * Instance a box repeatedly with per-instance position/rotation/scale.
 *
 * `geometry` is passed in and SHARED between every instanced box in the
 * stadium: `InstancedMesh` keeps its per-instance matrices on the mesh, not on
 * the geometry, so one unit cube serves the vomitories, the truss, the masts,
 * the lamp banks and the floodlights between them.
 */
const instanceBoxes = (
  name: string,
  geometry: BoxGeometry,
  material: MeshStandardMaterial | MeshBasicMaterial,
  placements: readonly { p: Vector3; e: Euler; s: Vector3 }[]
): InstancedMesh => {
  const mesh = new InstancedMesh(geometry, material, placements.length);
  mesh.name = name;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.frustumCulled = false;
  const matrix = new Matrix4();
  const quaternion = new Quaternion();
  for (let i = 0; i < placements.length; i++) {
    const { p, e, s } = placements[i];
    quaternion.setFromEuler(e);
    matrix.compose(p, quaternion, s);
    mesh.setMatrixAt(i, matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
};

/**
 * Vomitories: the dark stairway gaps cut through each tier. They are what stops
 * the seating from reading as a single unbroken texture, and they sit exactly
 * where `crowdSeats` left holes — both derive the positions from the same
 * `vomitories` count on the same `TierLayout`.
 */
const vomitoryPlacements = (): { p: Vector3; e: Euler; s: Vector3 }[] => {
  const out: { p: Vector3; e: Euler; s: Vector3 }[] = [];
  for (const tier of TIERS) {
    const depth = tier.rows * tier.run;
    const midOffset = tier.frontOffset + depth / 2;
    const midY = tier.frontY + (tier.rows * tier.rise) / 2;
    const rake = Math.atan2(tier.rise, tier.run);
    const samples = bowlSamples(tier.vomitories, midOffset);
    for (const sample of samples) {
      const p = bowlPoint(midOffset, sample);
      out.push({
        p: new Vector3(p.x, midY + 14, p.z),
        e: new Euler(rake, Math.atan2(-p.nx, -p.nz), 0, 'YXZ'),
        s: new Vector3(tier.vomitoryWidth, 34, Math.hypot(depth, tier.rows * tier.rise)),
      });
    }
  }
  return out;
};

/**
 * The roof truss: radial rafters plus three chord rings, all one instanced box.
 *
 * A truss is the cheapest way to make a roof read as a structure rather than as
 * a lid, and it is the thing you look at when the camera is up near the ceiling
 * — which is a view this game produces constantly.
 */
const trussPlacements = (): { p: Vector3; e: Euler; s: Vector3 }[] => {
  const out: { p: Vector3; e: Euler; s: Vector3 }[] = [];
  const inner = STADIUM.roofInner;
  const outer = STADIUM.roofOuter;
  const span = outer.offset - inner.offset;
  const rise = outer.y - inner.y;
  const rake = Math.atan2(rise, span);
  const length = Math.hypot(span, rise);

  // Rafters, running inward-to-outward under the roof deck.
  const rafters = 56;
  const midOffset = (inner.offset + outer.offset) / 2;
  for (const sample of bowlSamples(rafters, midOffset)) {
    const p = bowlPoint(midOffset, sample);
    out.push({
      p: new Vector3(p.x, (inner.y + outer.y) / 2 - 60, p.z),
      e: new Euler(rake, Math.atan2(-p.nx, -p.nz), 0, 'YXZ'),
      s: new Vector3(34, 46, length),
    });
  }

  // Overhead catwalks, straight across the arena rather than around the bowl.
  // A yaw of PI/2 turns the box's long local Z onto world X.
  const spanX = ARENA.halfWidth + STADIUM.catwalkOverhang;
  const spanZ = ARENA.halfLength + STADIUM.catwalkOverhang;
  for (const z of [-3800, -1900, 0, 1900, 3800]) {
    out.push({
      p: new Vector3(0, STADIUM.catwalkY, z),
      e: new Euler(0, Math.PI / 2, 0, 'YXZ'),
      s: new Vector3(46, 64, spanX * 2),
    });
  }
  for (const x of [-2600, -900, 900, 2600]) {
    out.push({
      p: new Vector3(x, STADIUM.catwalkY + 90, 0),
      e: new Euler(0, 0, 0, 'YXZ'),
      s: new Vector3(38, 44, spanZ * 2),
    });
  }

  // Chord rings: short segments laid end to end around three radii.
  for (const k of [0.12, 0.5, 0.88]) {
    const offset = inner.offset + span * k;
    const y = inner.y + rise * k - 105;
    const segments = 56;
    const samples = bowlSamples(segments, offset);
    for (let i = 0; i < samples.length; i++) {
      const a = bowlPoint(offset, samples[i]);
      const b = bowlPoint(offset, samples[(i + 1) % samples.length]);
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz);
      out.push({
        p: new Vector3((a.x + b.x) / 2, y, (a.z + b.z) / 2),
        e: new Euler(0, Math.atan2(dx, dz), 0, 'YXZ'),
        s: new Vector3(24, 24, len * 1.02),
      });
    }
  }
  return out;
};

/** Corner-run centres of the outline at `offset` — the four floodlight sites. */
export const cornerSites = (offset: number): { x: number; z: number; yaw: number }[] =>
  [0, 2, 4, 6].map((edge) => {
    const p = bowlPoint(offset, { edge, f: 0.5 });
    return { x: p.x, z: p.z, yaw: Math.atan2(-p.nx, -p.nz) };
  });

/** End-wall centres of the outline at `offset` — where the big screens hang. */
export const endSites = (offset: number): { x: number; z: number; yaw: number }[] =>
  [1, 5].map((edge) => {
    const p = bowlPoint(offset, { edge, f: 0.5 });
    return { x: p.x, z: p.z, yaw: Math.atan2(-p.nx, -p.nz) };
  });

// ---------------------------------------------------------------------------
// The environment
// ---------------------------------------------------------------------------

export interface StadiumEnvironment {
  group: Group;
  crowd: CrowdVisual;
  skyline: SkylineVisual;
  stars: StarFieldVisual;
  /** The night sky gradient, used as `scene.background`. */
  skyTexture: ReturnType<typeof makeSkyTexture>;
  /**
   * Drive the crowd shimmer and keep the star sphere on the camera. `time` is
   * seconds and the whole thing is deterministic in it.
   */
  update(time: number, dt: number, camera?: Vector3): void;
  /** Set the crowd off; `team` is who scored. */
  celebrate(team: 0 | 1): void;
  dispose(): void;
}

export interface StadiumOptions {
  crowdSeed?: number;
  skylineSeed?: number;
  /** Skip the crowd and the city. Only used by the perf comparison. */
  minimal?: boolean;
}

export const buildStadium = (options: StadiumOptions = {}): StadiumEnvironment => {
  const group = new Group();
  group.name = 'stadium';

  const samples = bowlSamples(STADIUM.samples, STADIUM.lowerFront.offset);
  const disposables: { dispose(): void }[] = [];
  const track = <T extends { dispose(): void }>(t: T): T => {
    disposables.push(t);
    return t;
  };

  // One unit cube behind every instanced box in the stadium.
  const unitBox = track(new BoxGeometry(1, 1, 1));

  // --- the bowl itself ------------------------------------------------------
  // One loft, one material, one draw call for the entire seating structure.
  // DoubleSide because the same surface is seen from inside the bowl (the
  // treads) and from outside (the skin), and a second loft to get the winding
  // right would double the geometry to save nothing.
  const concrete = track(
    new MeshStandardMaterial({
      color: PALETTE_STADIUM.concrete,
      roughness: 0.92,
      metalness: 0.05,
      side: DoubleSide,
      envMapIntensity: 0.55,
    })
  );
  const bowlGeometry = track(loftGeometry(bowlProfile(), samples));
  const bowl = new Mesh(bowlGeometry, concrete);
  bowl.name = 'stadium-bowl';
  bowl.castShadow = false;
  bowl.receiveShadow = false;
  group.add(bowl);

  // --- LED advertising ring -------------------------------------------------
  // Emissive, team-tinted at the ends, and the first thing the eye finds
  // through the glass at pitch level.
  const ledGeometry = track(
    loftGeometry(
      [
        { offset: STADIUM.ledInner.offset, y: STADIUM.ledInner.y },
        { offset: STADIUM.ledOuter.offset, y: STADIUM.ledOuter.y },
      ],
      samples
    )
  );
  applyEndTint(ledGeometry);
  const ledMaterial = track(
    new MeshBasicMaterial({ vertexColors: true, side: DoubleSide, fog: true })
  );
  const led = new Mesh(ledGeometry, ledMaterial);
  led.name = 'stadium-led-ring';
  group.add(led);

  // --- banner fascia --------------------------------------------------------
  const bannerTexture = track(makeBannerTexture());
  bannerTexture.repeat.set(26, 1);
  const bannerMaterial = track(
    new MeshBasicMaterial({ map: bannerTexture, side: FrontSide, fog: true })
  );
  const bannerGeometry = track(
    loftGeometry(
      [
        { offset: STADIUM.lowerBack.offset - 8, y: STADIUM.lowerBack.y + 60 },
        { offset: STADIUM.lowerBack.offset - 8, y: STADIUM.fasciaTopY - 60 },
      ],
      samples
    )
  );
  applyBandUv(bannerGeometry, samples.length);
  const banner = new Mesh(bannerGeometry, bannerMaterial);
  banner.name = 'stadium-banner';
  group.add(banner);

  // --- vomitories -----------------------------------------------------------
  const vomMaterial = track(
    new MeshStandardMaterial({
      color: PALETTE_STADIUM.concreteDark,
      roughness: 1,
      metalness: 0,
      side: DoubleSide,
    })
  );
  const vomitories = instanceBoxes('stadium-vomitories', unitBox, vomMaterial, vomitoryPlacements());
  group.add(vomitories);

  // --- roof and truss -------------------------------------------------------
  const roofMaterial = track(
    new MeshStandardMaterial({
      color: PALETTE_STADIUM.roof,
      roughness: 0.7,
      metalness: 0.35,
      side: DoubleSide,
      envMapIntensity: 0.4,
    })
  );
  const roofGeometry = track(loftGeometry(roofProfile(), samples));
  const roof = new Mesh(roofGeometry, roofMaterial);
  roof.name = 'stadium-roof';
  group.add(roof);

  const steel = track(
    new MeshStandardMaterial({
      color: PALETTE_STADIUM.steel,
      roughness: 0.45,
      metalness: 0.85,
      envMapIntensity: 1.1,
    })
  );
  const truss = instanceBoxes('stadium-truss', unitBox, steel, trussPlacements());
  group.add(truss);

  // --- roof lamps -----------------------------------------------------------
  // A ring of emissive strips along the roof's inner edge. They light nothing
  // (the key light and the environment map do that) — they exist so the source
  // of the light on the pitch is visible, which is what an unlit procedural
  // stadium is always missing.
  const lampMaterial = track(new MeshBasicMaterial({ color: PALETTE_STADIUM.lamp, fog: false }));
  const lampPlacements: { p: Vector3; e: Euler; s: Vector3 }[] = [];
  for (const sample of bowlSamples(52, STADIUM.roofInner.offset)) {
    const p = bowlPoint(STADIUM.roofInner.offset + 90, sample);
    lampPlacements.push({
      p: new Vector3(p.x, STADIUM.roofInner.y - 40, p.z),
      e: new Euler(0, Math.atan2(-p.nx, -p.nz), 0, 'YXZ'),
      s: new Vector3(360, 26, 60),
    });
  }
  // Strip lights slung under the overhead catwalks. These are the sources the
  // pitch's baked pools and the environment map both imply, made visible.
  for (const z of [-3800, -1900, 0, 1900, 3800]) {
    for (let i = -3; i <= 3; i++) {
      lampPlacements.push({
        p: new Vector3(i * 1150, STADIUM.catwalkY - 60, z),
        e: new Euler(0, Math.PI / 2, 0, 'YXZ'),
        s: new Vector3(420, 22, 90),
      });
    }
  }
  const lamps = instanceBoxes('stadium-roof-lamps', unitBox, lampMaterial, lampPlacements);
  group.add(lamps);

  // --- floodlight towers ----------------------------------------------------
  const sites = cornerSites(STADIUM.mastOffset);
  const mastPlacements = sites.map((s) => ({
    p: new Vector3(s.x, STADIUM.mastHeight / 2 + STADIUM.groundY, s.z),
    e: new Euler(0, s.yaw, 0, 'YXZ'),
    s: new Vector3(120, STADIUM.mastHeight, 120),
  }));
  const masts = instanceBoxes('stadium-masts', unitBox, steel, mastPlacements);
  group.add(masts);

  // Lamp banks: 4 towers x 12 lamps, all one instanced mesh, all emissive.
  const bankPlacements: { p: Vector3; e: Euler; s: Vector3 }[] = [];
  for (const s of sites) {
    // Tilt the head down toward the middle of the pitch.
    const pitchDown = -0.55;
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 4; col++) {
        const across = (col - 1.5) * 200;
        const tx = -Math.sin(s.yaw + Math.PI / 2);
        const tz = -Math.cos(s.yaw + Math.PI / 2);
        bankPlacements.push({
          p: new Vector3(
            s.x + tx * across,
            STADIUM.mastHeadY + row * 190,
            s.z + tz * across
          ),
          e: new Euler(pitchDown, s.yaw, 0, 'YXZ'),
          s: new Vector3(170, 150, 40),
        });
      }
    }
  }
  const banks = instanceBoxes('stadium-floodlamps', unitBox, lampMaterial, bankPlacements);
  group.add(banks);

  // Additive glow quads, one per tower, so the lamps bloom instead of being
  // twelve hard white rectangles at 8,000 uu.
  const glowMaterial = track(
    new MeshBasicMaterial({
      color: 0xffe9bf,
      transparent: true,
      opacity: 0.28,
      blending: AdditiveBlending,
      depthWrite: false,
      fog: false,
      side: DoubleSide,
    })
  );
  const glowGeometry = track(new PlaneGeometry(1, 1));
  const glow = new InstancedMesh(glowGeometry, glowMaterial, sites.length);
  glow.name = 'stadium-floodglow';
  glow.frustumCulled = false;
  glow.renderOrder = 3;
  {
    const matrix = new Matrix4();
    const quaternion = new Quaternion();
    sites.forEach((s, i) => {
      quaternion.setFromEuler(new Euler(-0.55, s.yaw, 0, 'YXZ'));
      matrix.compose(
        new Vector3(s.x, STADIUM.mastHeadY + 190, s.z),
        quaternion,
        new Vector3(1500, 1100, 1)
      );
      glow.setMatrixAt(i, matrix);
    });
    glow.instanceMatrix.needsUpdate = true;
  }
  group.add(glow);

  // --- big screens ----------------------------------------------------------
  const screenTexture = track(makeScreenTexture());
  const screenMaterial = track(
    new MeshBasicMaterial({ map: screenTexture, side: FrontSide, fog: true })
  );
  const screenGeometry = track(new PlaneGeometry(STADIUM.screenWidth, STADIUM.screenHeight));
  const screenFrameMaterial = track(
    new MeshStandardMaterial({ color: 0x0b0e16, roughness: 0.6, metalness: 0.5, side: DoubleSide })
  );
  const screenFrameGeometry = track(
    new BoxGeometry(STADIUM.screenWidth + 130, STADIUM.screenHeight + 130, 90)
  );
  for (const site of endSites(STADIUM.screenOffset)) {
    const frame = new Mesh(screenFrameGeometry, screenFrameMaterial);
    frame.position.set(site.x, STADIUM.screenCentreY, site.z);
    frame.rotation.y = site.yaw;
    group.add(frame);

    // `yaw` turns local +Z to (sin, cos), which is the direction the panel
    // faces, i.e. toward the pitch — so the panel sits that far IN FRONT of the
    // frame's centre, not behind it.
    const panel = new Mesh(screenGeometry, screenMaterial);
    panel.position.set(
      site.x + Math.sin(site.yaw) * 56,
      STADIUM.screenCentreY,
      site.z + Math.cos(site.yaw) * 56
    );
    panel.rotation.y = site.yaw;
    group.add(panel);
  }

  // --- ground and sky -------------------------------------------------------
  const groundGeometry = track(new CircleGeometry(STADIUM.groundRadius, 72));
  const groundMaterial = track(new MeshBasicMaterial({ color: 0x080b13, side: FrontSide }));
  const ground = new Mesh(groundGeometry, groundMaterial);
  ground.name = 'stadium-ground';
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = STADIUM.groundY - 6;
  group.add(ground);

  const skyline = buildSkyline(options.skylineSeed);
  group.add(skyline.mesh);

  const crowd = buildCrowd({ seed: options.crowdSeed });
  group.add(crowd.mesh);

  const stars = buildStars(options.skylineSeed);
  group.add(stars.points);

  const skyTexture = makeSkyTexture();

  return {
    group,
    crowd,
    skyline,
    stars,
    skyTexture,
    update(time: number, dt: number, camera?: Vector3): void {
      crowd.update(time, dt);
      if (camera) stars.follow(camera.x, camera.y, camera.z);
    },
    celebrate(team: 0 | 1): void {
      crowd.cheer(team);
    },
    dispose(): void {
      crowd.dispose();
      skyline.dispose();
      stars.dispose();
      skyTexture.dispose();
      for (const d of disposables) d.dispose();
    },
  };
};

// ---------------------------------------------------------------------------
// Attribute helpers
// ---------------------------------------------------------------------------

/**
 * Tint a lofted ring toward each team's colour at its own end.
 *
 * Blue defends -Y in the physics frame, which is +Z here — the single fact this
 * whole file has to get right, and the reason the tests check the sign rather
 * than trusting the comment.
 */
export const applyEndTint = (geometry: BufferGeometry): void => {
  const position = geometry.getAttribute('position');
  const colors = new Float32Array(position.count * 3);
  const blue = new Color(TEAM_COLORS[0]);
  const orange = new Color(TEAM_COLORS[1]);
  const neutral = new Color(0x6d7d99);
  const c = new Color();
  for (let i = 0; i < position.count; i++) {
    const z = position.getZ(i);
    const t = Math.min(1, Math.max(0, (Math.abs(z) - 2400) / 3000)) * 0.85;
    c.copy(neutral).lerp(z > 0 ? blue : orange, t);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  geometry.setAttribute('color', new BufferAttribute(colors, 3));
};

/**
 * UVs for a two-ring band loft: u runs around the perimeter, v across the band.
 * `loftBowl` emits six vertices per quad in a fixed order, which is what makes
 * this a lookup rather than a projection.
 */
export const applyBandUv = (geometry: BufferGeometry, samples: number): void => {
  const position = geometry.getAttribute('position');
  const uvs = new Float32Array(position.count * 2);
  // Per quad: (r,i) (r,j) (r+1,j) (r,i) (r+1,j) (r+1,i)
  const pattern: [number, number][] = [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 0],
    [1, 1],
    [0, 1],
  ];
  for (let v = 0; v < position.count; v++) {
    const quad = Math.floor(v / 6);
    const [du, dv] = pattern[v % 6];
    uvs[v * 2] = (quad + du) / samples;
    uvs[v * 2 + 1] = dv;
  }
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
};
